import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface BreadcrumbItem {
  label: string
  path: string
  isLast: boolean
}

const Breadcrumbs: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathname = location.pathname

    // Define custom breadcrumb patterns
    if (pathname === '/home' || pathname === '/') {
      return [{ label: t('breadcrumbs.home'), path: '/home', isLast: true }]
    }

    if (pathname === '/sessions') {
      return [
        { label: t('breadcrumbs.schedule'), path: '/sessions', isLast: false },
        { label: t('breadcrumbs.sessionList'), path: '/sessions', isLast: true }
      ]
    }

    if (pathname === '/sessions/create') {
      return [
        { label: t('breadcrumbs.schedule'), path: '/sessions', isLast: false },
        { label: t('breadcrumbs.createSession'), path: '/sessions/create', isLast: true }
      ]
    }

    // Handle edit session route
    if (pathname.match(/^\/sessions\/[^\/]+\/edit$/)) {
      return [
        { label: t('breadcrumbs.schedule'), path: '/sessions', isLast: false },
        { label: t('breadcrumbs.editSession'), path: pathname, isLast: true }
      ]
    }

    // Handle session details route
    if (pathname.match(/^\/sessions\/[^\/]+$/) && pathname !== '/sessions') {
      return [
        { label: t('breadcrumbs.schedule'), path: '/sessions', isLast: false },
        { label: t('breadcrumbs.sessionDetails'), path: pathname, isLast: true }
      ]
    }

    // Members routes
    if (pathname === '/members') {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.memberList'), path: '/members', isLast: true }
      ]
    }

    if (pathname === '/members/create') {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.createMember'), path: '/members/create', isLast: true }
      ]
    }

    if (pathname.match(/^\/members\/[^\/]+\/edit$/) && !pathname.includes('/groups/')) {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.editMember'), path: pathname, isLast: true }
      ]
    }

    if (pathname.match(/^\/members\/[^\/]+$/) && pathname !== '/members' && !pathname.includes('/groups')) {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.memberDetails'), path: pathname, isLast: true }
      ]
    }

    // Group routes
    if (pathname === '/members/groups') {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.groups'), path: '/members/groups', isLast: true }
      ]
    }

    if (pathname === '/members/groups/create') {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.groups'), path: '/members/groups', isLast: false },
        { label: t('breadcrumbs.createGroup'), path: '/members/groups/create', isLast: true }
      ]
    }

    if (pathname.match(/^\/members\/groups\/[^\/]+\/edit$/)) {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.groups'), path: '/members/groups', isLast: false },
        { label: t('breadcrumbs.editGroup'), path: pathname, isLast: true }
      ]
    }

    if (pathname.match(/^\/members\/groups\/[^\/]+$/) && pathname !== '/members/groups') {
      return [
        { label: t('breadcrumbs.members'), path: '/members', isLast: false },
        { label: t('breadcrumbs.groups'), path: '/members/groups', isLast: false },
        { label: t('breadcrumbs.groupDetails'), path: pathname, isLast: true }
      ]
    }

    // Fallback for any other paths
    const pathSegments = pathname.split('/').filter(segment => segment !== '')
    const breadcrumbs: BreadcrumbItem[] = [
      { label: t('breadcrumbs.home'), path: '/home', isLast: false }
    ]

    const pathLabels: { [key: string]: string } = {
      'home': t('breadcrumbs.home'),
      'sessions': t('breadcrumbs.schedule'),
      'create': t('breadcrumbs.createSession'),
      'edit': t('breadcrumbs.editSession')
    }

    let currentPath = ''
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`
      const isLast = index === pathSegments.length - 1
      const label = pathLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)

      if (segment !== 'home') {
        breadcrumbs.push({
          label,
          path: currentPath,
          isLast
        })
      }
    })

    if (breadcrumbs.length > 0) {
      breadcrumbs[breadcrumbs.length - 1].isLast = true
    }

    return breadcrumbs
  }

  const breadcrumbs = getBreadcrumbs()

  return (
    <nav role="navigation">
      <ul className="breadcrumbs flex flex-wrap items-center">
        {breadcrumbs.map((breadcrumb, index) => (
          <li key={`${breadcrumb.path}-${index}`} className="breadcrumbs-item">
            <Link
              className="breadcrumbs-link"
              to={breadcrumb.path}
              aria-current={breadcrumb.isLast ? "page" : undefined}
            >
              {breadcrumb.label}
            </Link>
            {!breadcrumb.isLast && (
              <svg
                className="block"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.75781 7.03035C10.1043 6.9375 10.4749 7.03645 10.7285 7.29011L14.6982 11.2901C14.8875 11.4779 14.9941 11.7335 14.9941 12.0001C14.9941 12.2666 14.8875 12.5223 14.6982 12.71L10.6982 16.71C10.512 16.8948 10.2604 16.999 9.99805 17.0001C9.73224 17.0016 9.4768 16.8972 9.28809 16.71C9.0988 16.5223 8.99219 16.2667 8.99219 16.0001C8.99221 15.7335 9.09879 15.4779 9.28809 15.2901L12.5781 12.0001L9.30762 8.71003C9.05419 8.45641 8.95509 8.08666 9.04785 7.74031C9.14067 7.39392 9.41148 7.12328 9.75781 7.03035Z"
                  className="fill-grey-30 dark:fill-grey-80"
                />
              </svg>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}

export default Breadcrumbs


